// frontend/src/components/paywall/PaywallModal.tsx

import React, { useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useUsage } from '../../contexts/UsageContext';
import { getPaywallVariant } from '../../types/paywall';
import { StatusCard } from './StatusCard';
import { UpgradeSection } from './UpgradeSection';
import { FAQSection } from './FAQSection';
import '../../styles/PaywallModal.css';

/**
 * Paywall Modal Props
 */
export interface PaywallModalProps {
  /**
   * Optional override for modal open state (for testing)
   * If not provided, uses paywallState.isOpen from context
   */
  isOpen?: boolean;

  /**
   * Optional override for close handler (for testing)
   * If not provided, uses closePaywall from context
   */
  onClose?: () => void;

  /**
   * Optional callback when upgrade is clicked
   * TODO Phase 3: Wire to Stripe integration
   */
  onUpgradeClick?: () => void;
}

/**
 * Paywall Modal Component
 * Displays when user hits usage limits (no credits or trial expired)
 *
 * Features:
 * - Three variants based on user state (trial/pro, credits/time)
 * - Responsive design (mobile, tablet, desktop)
 * - Accessible (ARIA labels, keyboard navigation)
 * - Animated entrance
 */
export const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen: isOpenProp,
  onClose: onCloseProp,
  onUpgradeClick,
}) => {
  const {
    paywallState,
    closePaywall,
    usage,
    trialDaysRemaining,
    refreshUsage,
  } = useUsage();

  // Use prop overrides for testing, fallback to context
  const isOpen = isOpenProp !== undefined ? isOpenProp : paywallState.isOpen;
  const handleClose = onCloseProp || closePaywall;

  /**
   * Handle successful redemption code - refresh usage and close modal
   */
  const handleRedemptionSuccess = useCallback(async () => {
    await refreshUsage();  // Refresh to get new Pro status
    closePaywall();        // Close the modal
  }, [refreshUsage, closePaywall]);

  // Refs for focus management
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  /**
   * Focus management: Move focus to modal on open, restore on close
   */
  useEffect(() => {
    if (isOpen) {
      // Store currently focused element to restore later
      previouslyFocusedElement.current = document.activeElement as HTMLElement;

      // Move focus to close button
      if (closeButtonRef.current) {
        closeButtonRef.current.focus();
      }
    } else {
      // Restore focus when modal closes
      if (previouslyFocusedElement.current) {
        previouslyFocusedElement.current.focus();
      }
    }
  }, [isOpen]);

  /**
   * Focus trap: Keep focus inside modal, cycle through focusable elements
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !modalContainerRef.current) return;

      // Get all focusable elements inside modal
      const focusableElements = modalContainerRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Shift+Tab: If on first element, move to last
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
      // Tab: If on last element, move to first
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  /**
   * Close modal on Escape key press
   */
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  /**
   * Lock body scroll when modal is open
   */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Don't render if closed or no usage data
  if (!isOpen || !usage) return null;

  // Determine modal variant based on usage state
  const variant = getPaywallVariant(usage, trialDaysRemaining);

  return ReactDOM.createPortal(
    <div
      className="paywall-modal-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
      aria-describedby="paywall-description"
    >
      <div
        ref={modalContainerRef}
        className="paywall-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          ref={closeButtonRef}
          className="paywall-close-button"
          onClick={handleClose}
          aria-label="Close paywall modal"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Status Card - shows current usage state */}
        <StatusCard
          variant={variant}
          usage={usage}
          trialDaysRemaining={trialDaysRemaining}
        />

        {/* Upgrade Section - Pro subscription CTA (only show for non-Pro users) */}
        {variant !== 'pro-credits-exhausted' && (
          <UpgradeSection
            onUpgradeClick={onUpgradeClick}
            onRedemptionSuccess={handleRedemptionSuccess}
          />
        )}

        {/* FAQ Section */}
        <FAQSection />
      </div>
    </div>,
    document.body
  );
};
